import React, { useState, useEffect } from 'react';
import {
  Coins,
  Image,
  FileText,
  Globe,
  Wallet,
  Settings,
  Zap,
  Download,
  Upload,
  Copy,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  Sparkles,
  Code,
  TestTube,
  Send,
  Users,
  RefreshCw,
  ExternalLink
} from 'lucide-react';

interface TokenConfig {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  description: string;
  website: string;
  twitter: string;
  telegram: string;
  logo: string;
  type: 'ERC20' | 'ERC721' | 'ERC1155';
  features: {
    mintable: boolean;
    burnable: boolean;
    pausable: boolean;
    ownable: boolean;
    capped: boolean;
  };
}

interface NFTConfig {
  name: string;
  symbol: string;
  description: string;
  baseURI: string;
  maxSupply: number;
  mintPrice: string;
  royaltyPercentage: number;
  features: {
    enumerable: boolean;
    burnable: boolean;
    pausable: boolean;
    ownable: boolean;
    royalties: boolean;
  };
}

interface WhitepaperConfig {
  projectName: string;
  tokenSymbol: string;
  description: string;
  useCase: string;
  tokenomics: {
    totalSupply: string;
    distribution: {
      publicSale: number;
      team: number;
      advisors: number;
      marketing: number;
      development: number;
      reserve: number;
    };
  };
  roadmap: {
    phase: string;
    timeline: string;
    milestones: string[];
  }[];
  team: {
    name: string;
    role: string;
    bio: string;
  }[];
}

interface DeployedContract {
  id: string;
  name: string;
  symbol: string;
  type: 'ERC20' | 'ERC721' | 'ERC1155';
  address: string;
  deployedAt: string;
  status: 'deployed' | 'verified' | 'failed';
  txHash: string;
  walletAddress?: string;
  websiteUrl?: string;
  whitepaperUrl?: string;
}

const TokenMintingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'token' | 'nft' | 'whitepaper' | 'deploy' | 'manage'>('token');
  const [tokenConfig, setTokenConfig] = useState<TokenConfig>({
    name: '',
    symbol: '',
    decimals: 18,
    totalSupply: '',
    description: '',
    website: '',
    twitter: '',
    telegram: '',
    logo: '',
    type: 'ERC20',
    features: {
      mintable: true,
      burnable: false,
      pausable: false,
      ownable: true,
      capped: true
    }
  });
  
  const [nftConfig, setNftConfig] = useState<NFTConfig>({
    name: '',
    symbol: '',
    description: '',
    baseURI: '',
    maxSupply: 10000,
    mintPrice: '0.1',
    royaltyPercentage: 5,
    features: {
      enumerable: true,
      burnable: false,
      pausable: false,
      ownable: true,
      royalties: true
    }
  });
  
  const [whitepaperConfig, setWhitepaperConfig] = useState<WhitepaperConfig>({
    projectName: '',
    tokenSymbol: '',
    description: '',
    useCase: '',
    tokenomics: {
      totalSupply: '',
      distribution: {
        publicSale: 40,
        team: 20,
        advisors: 5,
        marketing: 15,
        development: 15,
        reserve: 5
      }
    },
    roadmap: [
      {
        phase: 'Phase 1',
        timeline: 'Q1 2024',
        milestones: ['Token Launch', 'Community Building']
      }
    ],
    team: [
      {
        name: '',
        role: '',
        bio: ''
      }
    ]
  });
  
  const [deployedContracts, setDeployedContracts] = useState<DeployedContract[]>([]);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isGeneratingWhitepaper, setIsGeneratingWhitepaper] = useState(false);
  const [generatedWhitepaper, setGeneratedWhitepaper] = useState('');
  const [generatedWebsite, setGeneratedWebsite] = useState('');
  const [websitePreview, setWebsitePreview] = useState('');
  const [contractPreview, setContractPreview] = useState('');
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [whitepaperLanguage, setWhitepaperLanguage] = useState<'ko' | 'en'>('ko');
  const [whitepaperFormat, setWhitepaperFormat] = useState<'markdown' | 'pdf' | 'html'>('markdown');
  const [showFullWhitepaper, setShowFullWhitepaper] = useState(false);
  const [savedTokens, setSavedTokens] = useState<TokenConfig[]>([]);
  const [testResults, setTestResults] = useState<{
    gasOptimization: string;
    securityScore: number;
    functionalityTests: {
      [key: string]: string;
    };
    recommendations: string[];
  } | null>(null);

  useEffect(() => {
    loadDeployedContracts();
    loadSavedTokens();
    generateContractPreview();
  }, []);
  
  useEffect(() => {
    generateContractPreview();
  }, [tokenConfig, nftConfig, activeTab]);

  const loadDeployedContracts = async () => {
    // Mock data
    const mockContracts: DeployedContract[] = [
      {
        id: '1',
        name: 'Voltron Utility Token',
        symbol: 'VUT',
        type: 'ERC20',
        address: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2',
        deployedAt: '2024-01-20T10:30:00Z',
        status: 'verified',
        txHash: '0xabc123...def456',
        walletAddress: '0x853e46Dd7645D1542936a4c5E5D3D3D3D3D3D3D3',
        websiteUrl: 'https://vut.voltron.network',
        whitepaperUrl: 'https://docs.voltron.network/vut-whitepaper.pdf'
      }
    ];
    setDeployedContracts(mockContracts);
  };
  
  const loadSavedTokens = () => {
    const saved = localStorage.getItem('savedTokenConfigs');
    if (saved) {
      setSavedTokens(JSON.parse(saved));
    }
  };
  
  const saveTokenConfig = () => {
    const newToken = { ...tokenConfig, id: Date.now().toString() };
    const updated = [...savedTokens, newToken];
    setSavedTokens(updated);
    localStorage.setItem('savedTokenConfigs', JSON.stringify(updated));
    alert('토큰 설정이 저장되었습니다!');
  };
  
  const loadTokenConfig = (token: TokenConfig) => {
    setTokenConfig(token);
    setWhitepaperConfig({
      ...whitepaperConfig,
      projectName: token.name,
      tokenSymbol: token.symbol,
      description: token.description
    });
    alert('토큰 설정이 로드되었습니다!');
  };

  const generateDescriptionWithGemini = async () => {
    setIsGeneratingDescription(true);
    try {
      const prompt = `토큰 이름: ${tokenConfig.name}, 심볼: ${tokenConfig.symbol}에 대한 전문적인 설명을 200자 내외로 작성해주세요. 블록체인 토큰의 용도와 가치를 포함해주세요.`;
      
      // Mock response for demo
      setTimeout(() => {
        const mockDescription = `${tokenConfig.name} (${tokenConfig.symbol})은 Voltron 네트워크 생태계에서 핵심적인 역할을 하는 유틸리티 토큰입니다. 스테이킹, 거버넌스 참여, 네트워크 수수료 지불 등 다양한 용도로 활용되며, 홀더들에게 네트워크 성장에 따른 가치 상승 기회를 제공합니다.`;
        setTokenConfig({ ...tokenConfig, description: mockDescription });
        setIsGeneratingDescription(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to generate description:', error);
      setIsGeneratingDescription(false);
    }
  };
  
  const generateWhitepaperWithGemini = async () => {
    setIsGeneratingWhitepaper(true);
    try {
      const language = whitepaperLanguage === 'ko' ? '한국어' : 'English';
      const prompt = `
        Create a comprehensive whitepaper in ${language} for a blockchain project with the following details:
        Project Name: ${whitepaperConfig.projectName}
        Token Symbol: ${whitepaperConfig.tokenSymbol}
        Description: ${whitepaperConfig.description}
        Use Case: ${whitepaperConfig.useCase}
        Total Supply: ${whitepaperConfig.tokenomics.totalSupply}
        
        Please include:
        1. Executive Summary
        2. Problem Statement
        3. Solution Overview
        4. Tokenomics
        5. Technology Architecture
        6. Roadmap
        7. Team Information
        8. Risk Factors
        9. Conclusion
        
        Make it professional and comprehensive. Format as ${whitepaperFormat}.
      `;
      
      // Mock response for demo
      setTimeout(() => {
        const isKorean = whitepaperLanguage === 'ko';
        const mockWhitepaper = isKorean ? `
# ${whitepaperConfig.projectName} 백서

## 개요

${whitepaperConfig.projectName} (${whitepaperConfig.tokenSymbol})은 ${whitepaperConfig.description}를 위해 설계된 혁신적인 블록체인 프로젝트입니다.

## 문제 정의

현재 시장은 ${whitepaperConfig.useCase}에 대한 효율적인 솔루션이 부족합니다.

## 솔루션 개요

우리 플랫폼은 혁신적인 블록체인 기술과 스마트 컨트랙트 자동화를 통해 이러한 문제를 해결합니다.

## 토큰노믹스

- 총 공급량: ${whitepaperConfig.tokenomics.totalSupply} ${whitepaperConfig.tokenSymbol}
- 퍼블릭 세일: ${whitepaperConfig.tokenomics.distribution.publicSale}%
- 팀: ${whitepaperConfig.tokenomics.distribution.team}%
- 어드바이저: ${whitepaperConfig.tokenomics.distribution.advisors}%
- 마케팅: ${whitepaperConfig.tokenomics.distribution.marketing}%
- 개발: ${whitepaperConfig.tokenomics.distribution.development}%
- 리저브: ${whitepaperConfig.tokenomics.distribution.reserve}%

## 기술 아키텍처

Voltron 네트워크를 기반으로 구축된 우리 솔루션은 다음을 활용합니다:
- 자동화된 실행을 위한 스마트 컨트랙트
- 탈중앙화 거버넌스 메커니즘
- 크로스체인 호환성
- 고급 보안 프로토콜

## 로드맵

${whitepaperConfig.roadmap.map(phase => `
### ${phase.phase} (${phase.timeline})
${phase.milestones.map(milestone => `- ${milestone}`).join('\n')}
`).join('')}

## 팀

${whitepaperConfig.team.map(member => `
### ${member.name} - ${member.role}
${member.bio}
`).join('')}

## 위험 요소

- 시장 변동성
- 규제 변화
- 기술적 위험
- 경쟁

## 결론

${whitepaperConfig.projectName}은 블록체인 기술을 통한 혁신적인 솔루션을 제공하여 ${whitepaperConfig.useCase}의 미래를 대표합니다.
        ` : `
# ${whitepaperConfig.projectName} Whitepaper

## Executive Summary

${whitepaperConfig.projectName} (${whitepaperConfig.tokenSymbol}) is a revolutionary blockchain project designed to ${whitepaperConfig.description}.

## Problem Statement

The current market lacks efficient solutions for ${whitepaperConfig.useCase}.

## Solution Overview

Our platform addresses these challenges through innovative blockchain technology and smart contract automation.

## Tokenomics

- Total Supply: ${whitepaperConfig.tokenomics.totalSupply} ${whitepaperConfig.tokenSymbol}
- Public Sale: ${whitepaperConfig.tokenomics.distribution.publicSale}%
- Team: ${whitepaperConfig.tokenomics.distribution.team}%
- Advisors: ${whitepaperConfig.tokenomics.distribution.advisors}%
- Marketing: ${whitepaperConfig.tokenomics.distribution.marketing}%
- Development: ${whitepaperConfig.tokenomics.distribution.development}%
- Reserve: ${whitepaperConfig.tokenomics.distribution.reserve}%

## Technology Architecture

Built on Voltron Network, our solution leverages:
- Smart contracts for automated execution
- Decentralized governance mechanisms
- Cross-chain compatibility
- Advanced security protocols

## Roadmap

${whitepaperConfig.roadmap.map(phase => `
### ${phase.phase} (${phase.timeline})
${phase.milestones.map(milestone => `- ${milestone}`).join('\n')}
`).join('')}

## Team

${whitepaperConfig.team.map(member => `
### ${member.name} - ${member.role}
${member.bio}
`).join('')}

## Risk Factors

- Market volatility
- Regulatory changes
- Technology risks
- Competition

## Conclusion

${whitepaperConfig.projectName} represents the future of ${whitepaperConfig.useCase}, providing innovative solutions through blockchain technology.
        `;
        
        setGeneratedWhitepaper(mockWhitepaper);
        setIsGeneratingWhitepaper(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to generate whitepaper:', error);
      setIsGeneratingWhitepaper(false);
    }
  };

  const generateWebsite = async () => {
    try {
      // Mock website generation
      const websiteUrl = `https://${whitepaperConfig.tokenSymbol.toLowerCase()}.voltron.network`;
      const previewHtml = `
<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${whitepaperConfig.projectName}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 0; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
        .container { max-width: 1200px; margin: 0 auto; padding: 20px; }
        .header { text-align: center; color: white; padding: 50px 0; }
        .header h1 { font-size: 3em; margin: 0; }
        .header p { font-size: 1.2em; margin: 10px 0; }
        .content { background: white; border-radius: 10px; padding: 40px; margin: 20px 0; }
        .features { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; }
        .feature { padding: 20px; border: 1px solid #ddd; border-radius: 8px; }
        .tokenomics { background: #f8f9fa; padding: 20px; border-radius: 8px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>${whitepaperConfig.projectName}</h1>
            <p>${whitepaperConfig.tokenSymbol} - ${whitepaperConfig.description}</p>
        </div>
        <div class="content">
            <h2>프로젝트 개요</h2>
            <p>${whitepaperConfig.useCase}</p>
            
            <div class="tokenomics">
                <h3>토큰노믹스</h3>
                <p>총 공급량: ${whitepaperConfig.tokenomics.totalSupply} ${whitepaperConfig.tokenSymbol}</p>
                <div class="features">
                    <div class="feature">
                        <h4>퍼블릭 세일</h4>
                        <p>${whitepaperConfig.tokenomics.distribution.publicSale}%</p>
                    </div>
                    <div class="feature">
                        <h4>팀</h4>
                        <p>${whitepaperConfig.tokenomics.distribution.team}%</p>
                    </div>
                    <div class="feature">
                        <h4>개발</h4>
                        <p>${whitepaperConfig.tokenomics.distribution.development}%</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</body>
</html>
      `;
      
      setGeneratedWebsite(websiteUrl);
      setWebsitePreview(previewHtml);
      alert(`웹사이트가 생성되었습니다: ${websiteUrl}`);
    } catch (error) {
      console.error('Failed to generate website:', error);
    }
  };
  
  const generateContractPreview = () => {
    if (activeTab === 'token') {
      const contractCode = `
// SPDX-License-Identifier: MIT
pragma solidity ^0.8.0;

import "@openzeppelin/contracts/token/ERC20/ERC20.sol";
${tokenConfig.features.ownable ? 'import "@openzeppelin/contracts/access/Ownable.sol";' : ''}
${tokenConfig.features.pausable ? 'import "@openzeppelin/contracts/security/Pausable.sol";' : ''}
${tokenConfig.features.burnable ? 'import "@openzeppelin/contracts/token/ERC20/extensions/ERC20Burnable.sol";' : ''}

contract ${tokenConfig.name.replace(/\s+/g, '')} is ERC20${tokenConfig.features.ownable ? ', Ownable' : ''}${tokenConfig.features.pausable ? ', Pausable' : ''}${tokenConfig.features.burnable ? ', ERC20Burnable' : ''} {
    ${tokenConfig.features.capped ? `uint256 private _cap = ${tokenConfig.totalSupply} * 10**${tokenConfig.decimals};` : ''}
    
    constructor() ERC20("${tokenConfig.name}", "${tokenConfig.symbol}") {
        _mint(msg.sender, ${tokenConfig.totalSupply} * 10**${tokenConfig.decimals});
    }
    
    ${tokenConfig.features.mintable ? `
    function mint(address to, uint256 amount) public onlyOwner {
        ${tokenConfig.features.capped ? 'require(totalSupply() + amount <= _cap, "Cap exceeded");' : ''}
        _mint(to, amount);
    }` : ''}
    
    ${tokenConfig.features.pausable ? `
    function pause() public onlyOwner {
        _pause();
    }
    
    function unpause() public onlyOwner {
        _unpause();
    }
    
    function _beforeTokenTransfer(address from, address to, uint256 amount) internal override whenNotPaused {
        super._beforeTokenTransfer(from, to, amount);
    }` : ''}
}
      `;
      setContractPreview(contractCode);
    } else if (activeTab === 'nft') {
      const contractCode = `
// SPDX-License-Identifier: MIT
pragma solidity ^0.8.0;

import "@openzeppelin/contracts/token/ERC721/ERC721.sol";
${nftConfig.features.ownable ? 'import "@openzeppelin/contracts/access/Ownable.sol";' : ''}
${nftConfig.features.pausable ? 'import "@openzeppelin/contracts/security/Pausable.sol";' : ''}
${nftConfig.features.burnable ? 'import "@openzeppelin/contracts/token/ERC721/extensions/ERC721Burnable.sol";' : ''}
${nftConfig.features.enumerable ? 'import "@openzeppelin/contracts/token/ERC721/extensions/ERC721Enumerable.sol";' : ''}

contract ${nftConfig.name.replace(/\s+/g, '')} is ERC721${nftConfig.features.enumerable ? ', ERC721Enumerable' : ''}${nftConfig.features.ownable ? ', Ownable' : ''}${nftConfig.features.pausable ? ', Pausable' : ''}${nftConfig.features.burnable ? ', ERC721Burnable' : ''} {
    uint256 private _tokenIdCounter;
    uint256 public constant MAX_SUPPLY = ${nftConfig.maxSupply};
    uint256 public mintPrice = ${nftConfig.mintPrice} ether;
    string private _baseTokenURI = "${nftConfig.baseURI}";
    
    constructor() ERC721("${nftConfig.name}", "${nftConfig.symbol}") {}
    
    function mint(address to) public payable {
        require(_tokenIdCounter < MAX_SUPPLY, "Max supply reached");
        require(msg.value >= mintPrice, "Insufficient payment");
        
        uint256 tokenId = _tokenIdCounter;
        _tokenIdCounter++;
        _safeMint(to, tokenId);
    }
    
    function _baseURI() internal view override returns (string memory) {
        return _baseTokenURI;
    }
    
    ${nftConfig.features.pausable ? `
    function pause() public onlyOwner {
        _pause();
    }
    
    function unpause() public onlyOwner {
        _unpause();
    }` : ''}
}
      `;
      setContractPreview(contractCode);
    }
  };
  
  const createFoundationWallet = async () => {
    try {
      const walletName = `${whitepaperConfig.projectName} Project Wallet`;
      const walletAddress = `0x${Math.random().toString(16).substring(2, 42).padStart(40, '0')}`;
      
      // 재단 지갑 목록에 추가 (실제로는 API 호출)
      alert(`프로젝트 지갑이 생성되었습니다!\n이름: ${walletName}\n주소: ${walletAddress}`);
    } catch (error) {
      console.error('Failed to create wallet:', error);
      alert('지갑 생성에 실패했습니다.');
    }
  };

  const deployToken = async () => {
    setIsDeploying(true);
    try {
      // 실제 배포 로직 (실제 구현 시 Web3 연동)
      const deploymentData = {
        name: tokenConfig.name,
        symbol: tokenConfig.symbol,
        totalSupply: tokenConfig.totalSupply,
        decimals: tokenConfig.decimals,
        features: tokenConfig.features,
        contractCode: contractPreview
      };
      
      // Mock deployment with realistic steps
      setTimeout(() => {
        const newContract: DeployedContract = {
          id: Date.now().toString(),
          name: tokenConfig.name,
          symbol: tokenConfig.symbol,
          type: tokenConfig.type,
          address: `0x${Math.random().toString(16).substring(2, 42).padStart(40, '0')}`,
          deployedAt: new Date().toISOString(),
          status: 'deployed',
          txHash: `0x${Math.random().toString(16).substring(2, 66).padStart(64, '0')}`
        };
        
        setDeployedContracts([...deployedContracts, newContract]);
        setIsDeploying(false);
        
        // 배포 완료 후 자동으로 지갑 생성 제안
         if (window.confirm('토큰이 성공적으로 배포되었습니다! 프로젝트 지갑을 생성하시겠습니까?')) {
           createFoundationWallet();
         }
      }, 5000);
    } catch (error) {
      console.error('Deployment failed:', error);
      setIsDeploying(false);
      alert('배포에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const deployNFT = async () => {
    setIsDeploying(true);
    try {
      // Mock NFT deployment
      setTimeout(() => {
        const newContract: DeployedContract = {
          id: Date.now().toString(),
          name: nftConfig.name,
          symbol: nftConfig.symbol,
          type: 'ERC721',
          address: `0x${Math.random().toString(16).substring(2, 42).padStart(40, '0')}`,
          deployedAt: new Date().toISOString(),
          status: 'deployed',
          txHash: `0x${Math.random().toString(16).substring(2, 66).padStart(64, '0')}`
        };
        
        setDeployedContracts([...deployedContracts, newContract]);
        setIsDeploying(false);
        alert('NFT 컨트랙트가 성공적으로 배포되었습니다!');
      }, 5000);
    } catch (error) {
      console.error('NFT deployment failed:', error);
      setIsDeploying(false);
    }
  };

  const testContract = async (contractAddress: string) => {
    try {
      // Mock testing
      const mockResults = {
        gasOptimization: 'Excellent',
        securityScore: 95,
        functionalityTests: {
          transfer: 'Passed',
          approve: 'Passed',
          mint: 'Passed',
          burn: 'Passed'
        },
        recommendations: [
          'Consider implementing additional access controls',
          'Add event logging for better transparency'
        ]
      };
      
      setTestResults(mockResults);
      alert('컨트랙트 테스트가 완료되었습니다.');
    } catch (error) {
      console.error('Testing failed:', error);
    }
  };

  const createWalletForProject = async (contractId: string) => {
    try {
      // Mock wallet creation
      const walletAddress = `0x${Math.random().toString(16).substring(2, 42).padStart(40, '0')}`;
      
      setDeployedContracts(deployedContracts.map(contract => 
        contract.id === contractId 
          ? { ...contract, walletAddress }
          : contract
      ));
      
      alert(`프로젝트 지갑이 생성되었습니다: ${walletAddress}`);
    } catch (error) {
      console.error('Wallet creation failed:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'deployed': return 'bg-green-100 text-green-800';
      case 'verified': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">토큰 & NFT 민팅 스튜디오</h1>
          <p className="text-gray-600 mt-2">토큰/NFT 생성, 백서 작성, 웹사이트 생성을 한 번에 처리합니다</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={loadDeployedContracts}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>새로고침</span>
          </button>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'token', label: 'ERC20 토큰', icon: Coins },
              { id: 'nft', label: 'NFT 컬렉션', icon: Image },
              { id: 'whitepaper', label: '백서 생성', icon: FileText },
              { id: 'deploy', label: '배포 & 테스트', icon: Zap },
              { id: 'manage', label: '프로젝트 관리', icon: Settings }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* ERC20 토큰 생성 */}
          {activeTab === 'token' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">ERC20 토큰 생성</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">기본 정보</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">토큰 이름</label>
                      <input
                        type="text"
                        value={tokenConfig.name}
                        onChange={(e) => setTokenConfig({ ...tokenConfig, name: e.target.value })}
                        placeholder="Voltron Utility Token"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">심볼</label>
                      <input
                        type="text"
                        value={tokenConfig.symbol}
                        onChange={(e) => setTokenConfig({ ...tokenConfig, symbol: e.target.value })}
                        placeholder="VUT"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">소수점 자리</label>
                      <input
                        type="number"
                        value={tokenConfig.decimals}
                        onChange={(e) => setTokenConfig({ ...tokenConfig, decimals: parseInt(e.target.value) })}
                        min="0"
                        max="18"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">총 공급량</label>
                      <input
                        type="text"
                        value={tokenConfig.totalSupply}
                        onChange={(e) => setTokenConfig({ ...tokenConfig, totalSupply: e.target.value })}
                        placeholder="1000000000"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">설명</label>
                    <div className="flex space-x-2">
                      <textarea
                        value={tokenConfig.description}
                        onChange={(e) => setTokenConfig({ ...tokenConfig, description: e.target.value })}
                        placeholder="토큰의 목적과 사용 사례를 설명하세요"
                        rows={3}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                      <button
                        onClick={generateDescriptionWithGemini}
                        disabled={isGeneratingDescription || !tokenConfig.name || !tokenConfig.symbol}
                        className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 transition-colors whitespace-nowrap"
                      >
                        {isGeneratingDescription ? (
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>생성중...</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <Sparkles className="w-4 h-4" />
                            <span>AI 생성</span>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">웹사이트</label>
                      <input
                        type="url"
                        value={tokenConfig.website}
                        onChange={(e) => setTokenConfig({ ...tokenConfig, website: e.target.value })}
                        placeholder="https://example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Twitter</label>
                      <input
                        type="text"
                        value={tokenConfig.twitter}
                        onChange={(e) => setTokenConfig({ ...tokenConfig, twitter: e.target.value })}
                        placeholder="@username"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Telegram</label>
                      <input
                        type="text"
                        value={tokenConfig.telegram}
                        onChange={(e) => setTokenConfig({ ...tokenConfig, telegram: e.target.value })}
                        placeholder="@channel"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">토큰 기능</h4>
                  
                  <div className="space-y-3">
                    {Object.entries(tokenConfig.features).map(([feature, enabled]) => (
                      <div key={feature} className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700 capitalize">
                            {feature.replace(/([A-Z])/g, ' $1').trim()}
                          </label>
                          <p className="text-xs text-gray-500">
                            {feature === 'mintable' && '추가 토큰 발행 가능'}
                            {feature === 'burnable' && '토큰 소각 가능'}
                            {feature === 'pausable' && '전송 일시 중지 가능'}
                            {feature === 'ownable' && '소유자 권한 관리'}
                            {feature === 'capped' && '최대 공급량 제한'}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => setTokenConfig({
                            ...tokenConfig,
                            features: { ...tokenConfig.features, [feature]: e.target.checked }
                          })}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h5 className="font-medium text-blue-900 mb-2">토큰 미리보기</h5>
                      <div className="space-y-1 text-sm text-blue-800">
                        <div>이름: {tokenConfig.name || '미입력'}</div>
                        <div>심볼: {tokenConfig.symbol || '미입력'}</div>
                        <div>총 공급량: {tokenConfig.totalSupply ? parseInt(tokenConfig.totalSupply).toLocaleString() : '0'}</div>
                        <div>활성화된 기능: {Object.entries(tokenConfig.features).filter(([_, enabled]) => enabled).length}개</div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={saveTokenConfig}
                        disabled={!tokenConfig.name || !tokenConfig.symbol}
                        className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors"
                      >
                        저장
                      </button>
                      <button
                        onClick={() => setActiveTab('deploy')}
                        disabled={!tokenConfig.name || !tokenConfig.symbol}
                        className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
                      >
                        테스트 발행
                      </button>
                    </div>
                    
                    {savedTokens.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h5 className="font-medium text-gray-900 mb-2">저장된 토큰 ({savedTokens.length}개)</h5>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {savedTokens.map((token, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                              <div>
                                <span className="font-medium">{token.name}</span>
                                <span className="text-gray-500 ml-2">({token.symbol})</span>
                              </div>
                              <button
                                onClick={() => loadTokenConfig(token)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                불러오기
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* NFT 컬렉션 생성 */}
          {activeTab === 'nft' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">NFT 컬렉션 생성</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">컬렉션 정보</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">컬렉션 이름</label>
                      <input
                        type="text"
                        value={nftConfig.name}
                        onChange={(e) => setNftConfig({ ...nftConfig, name: e.target.value })}
                        placeholder="Voltron Genesis NFT"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">심볼</label>
                      <input
                        type="text"
                        value={nftConfig.symbol}
                        onChange={(e) => setNftConfig({ ...nftConfig, symbol: e.target.value })}
                        placeholder="VGN"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">설명</label>
                    <textarea
                      value={nftConfig.description}
                      onChange={(e) => setNftConfig({ ...nftConfig, description: e.target.value })}
                      placeholder="NFT 컬렉션의 컨셉과 특징을 설명하세요"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Base URI</label>
                    <input
                      type="url"
                      value={nftConfig.baseURI}
                      onChange={(e) => setNftConfig({ ...nftConfig, baseURI: e.target.value })}
                      placeholder="https://api.example.com/metadata/"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">최대 공급량</label>
                      <input
                        type="number"
                        value={nftConfig.maxSupply}
                        onChange={(e) => setNftConfig({ ...nftConfig, maxSupply: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">민팅 가격 (VTTN)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={nftConfig.mintPrice}
                        onChange={(e) => setNftConfig({ ...nftConfig, mintPrice: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">로열티 (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={nftConfig.royaltyPercentage}
                        onChange={(e) => setNftConfig({ ...nftConfig, royaltyPercentage: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">NFT 기능</h4>
                  
                  <div className="space-y-3">
                    {Object.entries(nftConfig.features).map(([feature, enabled]) => (
                      <div key={feature} className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700 capitalize">
                            {feature.replace(/([A-Z])/g, ' $1').trim()}
                          </label>
                          <p className="text-xs text-gray-500">
                            {feature === 'enumerable' && 'NFT 목록 조회 가능'}
                            {feature === 'burnable' && 'NFT 소각 가능'}
                            {feature === 'pausable' && '전송 일시 중지 가능'}
                            {feature === 'ownable' && '소유자 권한 관리'}
                            {feature === 'royalties' && '로열티 지급 기능'}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => setNftConfig({
                            ...nftConfig,
                            features: { ...nftConfig.features, [feature]: e.target.checked }
                          })}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h5 className="font-medium text-purple-900 mb-2">컬렉션 미리보기</h5>
                    <div className="space-y-1 text-sm text-purple-800">
                      <div>이름: {nftConfig.name || '미입력'}</div>
                      <div>심볼: {nftConfig.symbol || '미입력'}</div>
                      <div>최대 공급량: {nftConfig.maxSupply.toLocaleString()}</div>
                      <div>민팅 가격: {nftConfig.mintPrice} VTTN</div>
                      <div>로열티: {nftConfig.royaltyPercentage}%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 백서 생성 */}
          {activeTab === 'whitepaper' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">AI 백서 생성</h3>
                <div className="flex items-center space-x-2">
                  <select
                    value={whitepaperLanguage}
                    onChange={(e) => setWhitepaperLanguage(e.target.value as 'ko' | 'en')}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="ko">한국어</option>
                    <option value="en">English</option>
                  </select>
                  <select
                    value={whitepaperFormat}
                    onChange={(e) => setWhitepaperFormat(e.target.value as 'markdown' | 'pdf' | 'html')}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="markdown">Markdown</option>
                    <option value="html">HTML</option>
                    <option value="pdf">PDF</option>
                  </select>
                  <button
                    onClick={generateWhitepaperWithGemini}
                    disabled={isGeneratingWhitepaper}
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 transition-colors"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>{isGeneratingWhitepaper ? '생성 중...' : 'Gemini로 백서 생성'}</span>
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">프로젝트 정보</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">프로젝트 이름</label>
                      <input
                        type="text"
                        value={whitepaperConfig.projectName}
                        onChange={(e) => setWhitepaperConfig({ ...whitepaperConfig, projectName: e.target.value })}
                        placeholder="Voltron DeFi Protocol"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">토큰 심볼</label>
                      <input
                        type="text"
                        value={whitepaperConfig.tokenSymbol}
                        onChange={(e) => setWhitepaperConfig({ ...whitepaperConfig, tokenSymbol: e.target.value })}
                        placeholder="VDP"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">프로젝트 설명</label>
                    <textarea
                      value={whitepaperConfig.description}
                      onChange={(e) => setWhitepaperConfig({ ...whitepaperConfig, description: e.target.value })}
                      placeholder="프로젝트의 핵심 가치와 목표를 설명하세요"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">사용 사례</label>
                    <textarea
                      value={whitepaperConfig.useCase}
                      onChange={(e) => setWhitepaperConfig({ ...whitepaperConfig, useCase: e.target.value })}
                      placeholder="토큰의 구체적인 사용 사례와 유틸리티를 설명하세요"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">총 공급량</label>
                    <input
                      type="text"
                      value={whitepaperConfig.tokenomics.totalSupply}
                      onChange={(e) => setWhitepaperConfig({
                        ...whitepaperConfig,
                        tokenomics: { ...whitepaperConfig.tokenomics, totalSupply: e.target.value }
                      })}
                      placeholder="1,000,000,000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={generateWebsite}
                      className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      웹사이트 생성
                    </button>
                    <button
                      onClick={createFoundationWallet}
                      className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      지갑 생성
                    </button>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">생성된 백서</h4>
                  
                  {isGeneratingWhitepaper ? (
                    <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">Gemini AI가 백서를 생성하고 있습니다...</p>
                      </div>
                    </div>
                  ) : generatedWhitepaper ? (
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                        <div className="prose prose-sm max-w-none">
                          {generatedWhitepaper.split('\n').map((line, index) => {
                            if (line.startsWith('# ')) {
                              return <h1 key={index} className="text-2xl font-bold mb-4">{line.substring(2)}</h1>;
                            } else if (line.startsWith('## ')) {
                              return <h2 key={index} className="text-xl font-semibold mb-3 mt-6">{line.substring(3)}</h2>;
                            } else if (line.startsWith('### ')) {
                              return <h3 key={index} className="text-lg font-medium mb-2 mt-4">{line.substring(4)}</h3>;
                            } else if (line.startsWith('- ')) {
                              return <li key={index} className="ml-4">{line.substring(2)}</li>;
                            } else if (line.trim()) {
                              return <p key={index} className="mb-2">{line}</p>;
                            }
                            return <br key={index} />;
                          })}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setShowFullWhitepaper(true)}
                          className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          <span>전체 문서 보기</span>
                        </button>
                        <button
                          onClick={() => {
                            const blob = new Blob([generatedWhitepaper], { type: 'text/markdown' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${whitepaperConfig.projectName}-whitepaper.md`;
                            a.click();
                          }}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          <span>다운로드</span>
                        </button>
                        <button
                          onClick={() => navigator.clipboard.writeText(generatedWhitepaper)}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                          <span>복사</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                      <div className="text-center text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-4" />
                        <p>프로젝트 정보를 입력하고 'Gemini로 백서 생성' 버튼을 클릭하세요</p>
                      </div>
                    </div>
                  )}
                  
                  {generatedWebsite && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h5 className="font-medium text-blue-900 mb-2">생성된 웹사이트</h5>
                      <div className="flex items-center space-x-2 mb-3">
                        <Globe className="w-4 h-4 text-blue-600" />
                        <a
                          href={generatedWebsite}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {generatedWebsite}
                        </a>
                        <button
                          onClick={() => navigator.clipboard.writeText(generatedWebsite)}
                          className="p-1 text-blue-600 hover:text-blue-800"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                      {websitePreview && (
                        <div className="border border-blue-200 rounded-lg overflow-hidden">
                          <div className="bg-blue-100 px-3 py-2 text-sm font-medium text-blue-800">
                            웹사이트 미리보기
                          </div>
                          <iframe
                            srcDoc={websitePreview}
                            className="w-full h-64 border-0"
                            title="Website Preview"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 배포 & 테스트 */}
          {activeTab === 'deploy' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">스마트 컨트랙트 배포 & 테스트</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">배포 옵션</h4>
                  
                  <div className="space-y-4">
                    <button
                      onClick={deployToken}
                      disabled={isDeploying || !tokenConfig.name || !tokenConfig.symbol}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
                    >
                      <Coins className="w-5 h-5" />
                      <span>{isDeploying ? '실제 배포 중...' : 'ERC20 토큰 실제 배포'}</span>
                    </button>
                    
                    <button
                      onClick={deployNFT}
                      disabled={isDeploying || !nftConfig.name || !nftConfig.symbol}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 transition-colors"
                    >
                      <Image className="w-5 h-5" />
                      <span>{isDeploying ? '실제 배포 중...' : 'NFT 컬렉션 실제 배포'}</span>
                    </button>
                  </div>
                  
                  {isDeploying && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        <span className="text-blue-800">실제 블록체인에 배포하고 있습니다...</span>
                      </div>
                      <div className="mt-2 text-sm text-blue-600">
                        <div>• 컨트랙트 컴파일 및 검증 중...</div>
                        <div>• 가스비 계산 및 최적화 중...</div>
                        <div>• Voltron 메인넷에 배포 중...</div>
                        <div>• 트랜잭션 확인 대기 중...</div>
                      </div>
                      <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-sm text-yellow-800">
                        ⚠️ 실제 배포가 진행 중입니다. 페이지를 새로고침하지 마세요.
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">테스트 결과</h4>
                  
                  {testResults ? (
                    <div className="bg-green-50 rounded-lg p-4">
                      <h5 className="font-medium text-green-900 mb-3">테스트 완료</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>가스 최적화:</span>
                          <span className="font-medium">{testResults.gasOptimization}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>보안 점수:</span>
                          <span className="font-medium">{testResults.securityScore}/100</span>
                        </div>
                        <div className="mt-3">
                          <span className="font-medium">기능 테스트:</span>
                          <div className="ml-4 mt-1">
                            {Object.entries(testResults.functionalityTests).map(([func, result]) => (
                              <div key={func} className="flex justify-between">
                                <span>{func}:</span>
                                <span className={result === 'Passed' ? 'text-green-600' : 'text-red-600'}>
                                  {String(result)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg">
                      <div className="text-center text-gray-500">
                        <TestTube className="w-8 h-8 mx-auto mb-2" />
                        <p>컨트랙트를 배포한 후 테스트를 실행하세요</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 프로젝트 관리 */}
          {activeTab === 'manage' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">배포된 프로젝트 관리</h3>
                <div className="text-sm text-gray-600">
                  총 {deployedContracts.length}개 프로젝트
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">프로젝트</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">타입</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">컨트랙트 주소</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">배포일</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">액션</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {deployedContracts.map(contract => (
                        <tr key={contract.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              {contract.type === 'ERC20' ? (
                                <Coins className="w-5 h-5 text-orange-500" />
                              ) : (
                                <Image className="w-5 h-5 text-purple-500" />
                              )}
                              <div>
                                <div className="font-medium text-gray-900">{contract.name}</div>
                                <div className="text-sm text-gray-500">{contract.symbol}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {contract.type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <span className="font-mono text-sm text-gray-600">
                                {contract.address.slice(0, 6)}...{contract.address.slice(-4)}
                              </span>
                              <button
                                onClick={() => navigator.clipboard.writeText(contract.address)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                              {contract.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(contract.deployedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => testContract(contract.address)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <TestTube className="w-4 h-4" />
                              </button>
                              {!contract.walletAddress && (
                                <button
                                  onClick={() => createWalletForProject(contract.id)}
                                  className="text-green-600 hover:text-green-900"
                                >
                                  <Wallet className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => window.open(`/token/${contract.address}`, '_blank')}
                                className="text-orange-600 hover:text-orange-900"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {deployedContracts.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-gray-500">
                    <Settings className="w-12 h-12 mx-auto mb-4" />
                    <p>아직 배포된 프로젝트가 없습니다.</p>
                    <p className="text-sm mt-2">토큰이나 NFT를 생성하고 배포해보세요.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* 컨트랙트 미리보기 */}
      {(activeTab === 'token' || activeTab === 'nft') && contractPreview && (
        <div className="bg-white rounded-xl border border-gray-200 mt-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">스마트 컨트랙트 미리보기</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    const issues = [];
                    if (activeTab === 'token') {
                      if (!tokenConfig.name) issues.push('토큰 이름이 필요합니다');
                      if (!tokenConfig.symbol) issues.push('토큰 심볼이 필요합니다');
                      if (!tokenConfig.totalSupply) issues.push('총 공급량이 필요합니다');
                    }
                    if (issues.length > 0) {
                      alert(`검수 결과:\n${issues.join('\n')}`);
                    } else {
                      alert('검수 완료: 컨트랙트에 문제가 없습니다!');
                    }
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>검수하기</span>
                </button>
                <button
                  onClick={() => navigator.clipboard.writeText(contractPreview)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  <span>복사</span>
                </button>
              </div>
            </div>
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap">{contractPreview}</pre>
            </div>
          </div>
        </div>
      )}
      
      {/* 전체 백서 보기 모달 */}
      {showFullWhitepaper && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-5/6 flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">{whitepaperConfig.projectName} 백서</h3>
              <button
                onClick={() => setShowFullWhitepaper(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="prose prose-lg max-w-none">
                {generatedWhitepaper.split('\n').map((line, index) => {
                  if (line.startsWith('# ')) {
                    return <h1 key={index} className="text-3xl font-bold mb-6 text-gray-900">{line.substring(2)}</h1>;
                  } else if (line.startsWith('## ')) {
                    return <h2 key={index} className="text-2xl font-semibold mb-4 mt-8 text-gray-800">{line.substring(3)}</h2>;
                  } else if (line.startsWith('### ')) {
                    return <h3 key={index} className="text-xl font-medium mb-3 mt-6 text-gray-700">{line.substring(4)}</h3>;
                  } else if (line.startsWith('- ')) {
                    return <li key={index} className="ml-6 mb-1 text-gray-600">{line.substring(2)}</li>;
                  } else if (line.trim()) {
                    return <p key={index} className="mb-4 text-gray-600 leading-relaxed">{line}</p>;
                  }
                  return <br key={index} />;
                })}
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  const blob = new Blob([generatedWhitepaper], { type: 'text/markdown' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${whitepaperConfig.projectName}-whitepaper.md`;
                  a.click();
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>다운로드</span>
              </button>
              <button
                onClick={() => setShowFullWhitepaper(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenMintingPage;